/**
 * RFC 0224: cross-session reads, as tools. The pure projection a `read_session`
 * tool returns: a sibling session's recent transcript and/or its touched objects,
 * read-only, from that session's log.
 *
 * Pure and host-agnostic: it consumes the RFC 0215 log projections
 * (projectTranscript / projectObjects) and never touches the repo. The host (web /
 * CLI / Searugast) loads the peer doc and passes it in, so the run infra stays
 * host-agnostic (RFC 0224 §2.2).
 */

import { projectObjects } from '../../log/projection-objects'
import {
  projectTranscript,
  projectTranscriptFromHeads,
  type TranscriptMessage,
} from '../../log/projection-transcript'
import type { EntryId, LogDoc } from '../../log/types'

export interface SessionContextOptions {
  /** Display title for the peer session (its focus / name). */
  title?: string
  /** What to read: recent transcript, touched objects, or both (default). */
  read?: 'transcript' | 'objects' | 'both'
  /** Cap on transcript messages (most recent N; default 20). */
  maxMessages?: number
}

/** Flatten a transcript message's content blocks to a single line of text. */
function messageText(m: TranscriptMessage): string {
  const parts: string[] = []
  for (const block of m.content) {
    if (block.type === 'text') parts.push(block.text)
    else if (block.type === 'tool_use') parts.push(`[tool_use ${block.name}]`)
  }
  return parts.join(' ').trim()
}

/**
 * A branch inherits the conversation, not the parent's execution trace.
 *
 * Tool results can contain entire command outputs, screenshots and MCP payloads.
 * Copying those into the child's first user prompt both wastes its context and
 * makes operational details look like instructions it should continue. Keep
 * only prose here; the child can use `read_session` when it deliberately needs
 * the source session's tools or touched objects.
 */
function branchMessageText(m: TranscriptMessage): string {
  return m.content
    .filter(
      (block): block is Extract<typeof block, { type: 'text' }> =>
        block.type === 'text',
    )
    .map((block) => block.text)
    .join(' ')
    .trim()
}

/**
 * Project a read-only view of a peer session's log: its recent transcript and/or
 * the objects (files) it has touched. The shape an agent reads to coordinate with
 * a sibling session working the same branch.
 */
export function projectSessionContext(
  doc: LogDoc,
  o: SessionContextOptions = {},
): string {
  const read = o.read ?? 'both'
  const parts: string[] = [`Session "${o.title ?? 'peer'}" (read-only):`]

  if (read !== 'objects') {
    const recent = projectTranscript(doc).slice(-(o.maxMessages ?? 20))
    parts.push(...recent.map((m) => `- ${m.role}: ${messageText(m)}`))
  }
  if (read !== 'transcript') {
    for (const obj of projectObjects(doc).values()) {
      parts.push(`- ${obj.path} (${obj.objectType})`)
    }
  }

  return parts.join('\n')
}

/**
 * How many messages a branch's first prompt carries by default (RFC 0302 S4).
 *
 * Enough that the child knows what conversation it came out of, small enough
 * that fanning out four branches does not put four copies of a long transcript
 * through a provider. The child reads more with `read_session` if it needs to —
 * it has the source session id, and that is what recording it is for.
 */
const BRANCH_CONTEXT_MESSAGES = 12

/**
 * The seed a branch's first turn is given (RFC 0302 S4).
 *
 * A branch starts a FRESH harness conversation: Claude Code, Codex and Cursor
 * each resume one linear provider history, and two branches sharing a resume id
 * would leak into one another (3f). So the ancestor context is rendered INTO the
 * first prompt rather than resumed, and it is the ancestor closure of the SOURCE
 * HEADS — not the parent's latest transcript, which by the time this runs may
 * already carry the sibling answer this branch must not see.
 */
export function renderBranchContext(
  doc: LogDoc,
  heads: readonly EntryId[],
  o: { title?: string; maxMessages?: number } = {},
): string {
  const recent = projectTranscriptFromHeads(doc, heads)
    .filter((m) => m.role !== 'tool_result')
    .map((m) => ({ role: m.role, text: branchMessageText(m) }))
    .filter((m) => m.text.length > 0)
    .slice(-(o.maxMessages ?? BRANCH_CONTEXT_MESSAGES))
  if (recent.length === 0) return ''
  return [
    `The conversation this came out of, in "${o.title ?? 'the source session'}":`,
    ...recent.map((m) => `- ${m.role}: ${m.text}`),
  ].join('\n')
}

/**
 * The coordination signal `list_sessions` carries per peer (RFC 0224 §2.1, "the
 * workhorse"): the session's current focus (its latest user/run prompt) and the
 * files (objects) it has touched. One cheap call answers "who is doing what, on
 * which files," computed from the pure projections with no branch diff. The host
 * adds the per-session metadata it owns (sessionDocId, lastActiveAt).
 */
export interface SessionSummary {
  /** Current goal: the session's most recent user prompt (empty if none). */
  focus: string
  /** Paths of the objects (files) this session has touched. */
  filesChanged: string[]
}

export function projectSessionSummary(doc: LogDoc): SessionSummary {
  let focus = ''
  // The latest user message is the session's current goal.
  for (const m of projectTranscript(doc)) {
    if (m.role === 'user') {
      const text = messageText(m)
      if (text) focus = text
    }
  }
  const filesChanged = [...projectObjects(doc).values()].map((o) => o.path)
  return { focus, filesChanged }
}
